import apiClient, { isApiConfigured } from './axiosClient';
import { claims as mockClaims } from '../data/mockData';

/* -- 835 ERA (EDI) helpers ------------------------------------------------
   An 835 is a PAYMENT-level document: one payment header + one loop per claim
   on that check. So a check/EOP → all its claims; a single claim resolves to
   ITS check and still emits the full remittance (standard, importable). */
const money = (n) => Number(n || 0).toFixed(2);
const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Build a representative X12 835 from the claims on a check (demo/mock — not a
// production-validated file; the real 835 comes from the portal/EDI backend).
function buildMock835(checkClaims, { checkNumber, providerName, npi }) {
  const seg = [];
  const totalPaid = checkClaims.reduce((s, c) => s + Number(c.paidAmount || 0), 0);
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  seg.push(`ISA*00*          *00*          *ZZ*CENTRALHEALTH  *ZZ*${(npi || 'PROVIDER').padEnd(15).slice(0, 15)}*${today.slice(2)}*1200*^*00501*000000001*0*P*:`);
  seg.push('GS*HP*CENTRALHEALTH*PROVIDER*' + today + '*1200*1*X*005010X221A1');
  seg.push('ST*835*0001');
  seg.push(`BPR*I*${money(totalPaid)}*C*ACH*CCP*01*999999999*DA*123456789*1512345678**01*999988880*DA*98765*${today}`);
  seg.push(`TRN*1*${checkNumber || 'CHK-UNKNOWN'}*1512345678`);
  seg.push(`DTM*405*${today}`);
  seg.push('N1*PR*CENTRAL HEALTH PLANS');
  seg.push('N3*P.O. BOX 17337');
  seg.push('N4*AUSTIN*TX*78760');
  seg.push(`N1*PE*${(providerName || 'PROVIDER').toUpperCase()}*XX*${npi || ''}`);
  seg.push('LX*1');
  checkClaims.forEach((c) => {
    const statusCode = /denied/i.test(c.status) ? 4 : 1; // 1=processed as primary, 4=denied
    const [last = '', first = ''] = String(c.memberName || '').split(' ').reverse();
    seg.push(`CLP*${c.claimNumber}*${statusCode}*${money(c.billedAmount)}*${money(c.paidAmount)}*${money(c.memberResponsibility)}*MC*${c.eopNumber || ''}*11`);
    seg.push(`NM1*QC*1*${last.toUpperCase()}*${first.toUpperCase()}****MI*${c.memberId || ''}`);
    seg.push(`SVC*HC:${c.procedureCode || ''}*${money(c.billedAmount)}*${money(c.paidAmount)}`);
    seg.push(`DTM*472*${String(c.serviceDate || '').replace(/-/g, '')}`);
    if (statusCode === 4) seg.push('CAS*CO*4*' + money(c.billedAmount)); // denied → contractual
    else if (Number(c.memberResponsibility) > 0) seg.push('CAS*PR*3*' + money(c.memberResponsibility)); // copay
  });
  seg.push(`SE*${seg.length - 2}*0001`);
  seg.push('GE*1*1');
  seg.push('IEA*1*000000001');
  return seg.join('~\n') + '~\n';
}

export const eopApi = {
  /**
   * Download the 835 ERA (EDI) for a check/remittance. Pass { checkNumber } from
   * an EOP result, or { claimNumber } from a claim detail (resolves to its check
   * → the FULL remittance, all claims on that check). Live = portal/EDI backend;
   * otherwise a representative mock 835 is generated from bundled claims.
   * TODO(portal API): confirm the real 835 endpoint + params.
   */
  download835: async ({ checkNumber, claimNumber, billingProvider } = {}) => {
    if (isApiConfigured()) {
      const res = await apiClient.get('/api/eop/835', { // TODO confirm path
        params: { CheckNumber: checkNumber, ClaimNumber: claimNumber, BillingProvider: billingProvider, _: Date.now() },
        responseType: 'blob',
      });
      triggerDownload(res.data, `835_${checkNumber || claimNumber || 'remittance'}.edi`);
      return { ok: true };
    }
    // Mock: resolve the check (from claimNumber if needed), emit all its claims.
    let chk = checkNumber;
    if (!chk && claimNumber) chk = mockClaims.find((c) => c.claimNumber === claimNumber)?.checkNumber;
    const list = mockClaims.filter((c) => (chk ? c.checkNumber === chk : c.claimNumber === claimNumber));
    if (!list.length) throw new Error('No paid claims found for this remittance.');
    const p = list[0];
    const edi = buildMock835(list, { checkNumber: chk || p.checkNumber, providerName: p.providerName, npi: '1234567890' });
    triggerDownload(new Blob([edi], { type: 'application/edi-x12' }), `835_${chk || claimNumber || 'remittance'}.edi`);
    return { ok: true, count: list.length };
  },

    //searchEOP: async (params) => {
    //    const res = await apiClient.get(`/api/provider/eop?billingProvider=${params.billingProvider}&checkNo=${params.checkNumber}&serviceStart=${params.serviceStart}&serviceEnd=${params.serviceEnd}&groupId=${params.groupId}`);
    //    const data = res?.data;

        

    //    return {
    //        success: data?.Success,
    //        message: data?.Message,
    //        data: data?.Data || []   //  all properties preserved
    //    };
    //},
    searchEOP: async (params) => {
        const res = await apiClient.get(`/api/provider/eop2?billingProvider=${params.billingProvider}&checkNo=${params.checkNumber}&serviceStart=${params.serviceStart}&serviceEnd=${params.serviceEnd}&groupId=${params.groupId}&startIndex=${params.startIndex}&pageSize=${params.pageSize}&searchText=${params.searchText}&sortColumnName=${params.sortColumnName}&sortColumnDirection=${params.sortColumnDirection}`);
        const data = res?.data;



        return {
            success: data?.Success,
            message: data?.Message,
            data: data?.Data || []   //  all properties preserved
        };
    },
    requestEOP: async (params) => {
        const res = await apiClient.post(`/api/AzureProviderAPI/request-detail-eop`, {
            billingProvider: params.billingProvider,
            checkNumber: params.checkNumber,
            paidAmount: params.paidAmount,
            //eopType: "CheckEOP", //CsvFile|CheckEOP|Both
            eopType: params.eopType, //CsvFile|CheckEOP|Both
            userId: params.userId,
            token:params.token
        });
        const data = res?.data;

       

        return {
            success: data?.success,
            message: data?.message,
            data: data?.Data || []   //  all properties preserved
        };
    },
    

  //downloadEOP: (eopNumber) =>
    //  apiClient.get(`/eop/${eopNumber}/download`, { responseType: 'blob' }),



    downloadEOP: async (params) => {
        const res = await apiClient.get(`/api/AzureProviderAPI/detaileop-download?BillingProvider=${params.billingProvider}&CheckNumber=${params.checkNumber}&FileName=${params.fileName}`, {
            responseType: "blob"
        });

        return res;
        
    },
    downloadClaimEOP: async (params) => {
        const res = await apiClient.get(`/api/AzureProviderAPI/claimeop-download?BillingProvider=${params.billingProvider}&ClaimNumber=${params.claimNumber}&CheckNumber=${params.checkNumber}`, {
            responseType: "blob"
        });

        return res;
        
    },
    requestClaimEOP: async (params) => {
        const res = await apiClient.post(`/api/AzureProviderAPI/request-claim-eop`, {
            billingProvider: params.billingProvider,
            checkNumber: params.checkNumber,
            claimNumber: params.claimNumber,
            paidAmount: params.paidAmount,            
            userId: params.userId,
            token: params.token
        });
        const data = res?.data;

        return {
            success: data?.success,
            message: data?.message,
            data: data?.Data || []   //  all properties preserved
        };
    }
};
